import { NetworkStatus, gql } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import {
  Alert,
  Button,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { flow, groupBy, map, orderBy, sumBy } from 'lodash';
import React, { Fragment, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { Code } from 'src/components/code/Code';
import { FilterProvider } from 'src/components/common/filterProvider/FilterProvider';
import { Loading } from 'src/components/common/loading/Loading';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { EmptySelectionPrompt } from 'src/components/emptySelectionPrompt/EmptySelectionPrompt';
import { InsightsEnforcementWarning } from 'src/components/insightsEnforcementWarning/InsightsEnforcementWarning';
import { MetricsFilter } from 'src/components/metricsFilter/MetricsFilter';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import {
  TimeRangeOption,
  printTimeRangeLabel,
} from 'src/lib/config/timeRangeOptions';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

import { clientsRouteConfig } from '../routes';

import { Client } from './client/Client';
import { ClientDetails } from './clientDetails/ClientDetails';
import { UNIDENTIFIED_CLIENT } from './constants';

const clientsPageQuery = gql`
  query UI__ClientsPageQuery(
    $serviceId: ID!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $filter: ServiceQueryStatsFilter
  ) {
    service(id: $serviceId) {
      id
      account {
        id
      }
      stats: statsWindow(from: $timeFrom, to: $timeTo) {
        queryStats(filter: $filter) {
          metrics {
            requestsWithErrorsCount
            totalRequestCount
            registeredOperationCount
          }
          groupBy {
            clientName
            clientVersion
          }
        }
      }
    }
  }
`;

const APOLLO_CLIENT_INSTRUCTIONS = `import { ApolloClient } from 'apollo-client';

const client = new ApolloClient({
  ...
  name: 'web',
  version: '1.0'
});`;

const APOLLO_SERVER_INSTRUCTIONS = `const { ApolloServer } = require("apollo-server");
const { ApolloServerPluginUsageReporting } = require("apollo-server-core");

const server = new ApolloServer({
  ...
  plugins: [
    ApolloServerPluginUsageReporting({
      generateClientInfo: ({ request }) => {
        const { clientName, clientVersion } = userSuppliedLogic(request);
        return {
          clientName,
          clientVersion
        };
      }
    })
  ],
});`;

export const ClientsPage = () => {
  const [instructionModal, setInstructionModal] = useState(false);
  const { graphId, graphVariant, clientName, clientVersion, ...routeParams } =
    useRouteParams(clientsRouteConfig);
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  const initialClientName = React.useRef(clientName);
  const [hasScrolledToClient, setHasScrolledToClient] = React.useState(false);

  useTrackL1PageView('clientsL1', {
    graphRef: { graphId, graphVariant },
  });

  return (
    <FilterProvider>
      {({ timeFrom, timeTo, selectedRange, accountCapabilities, filter }) => (
        <Query<
          GraphQLTypes.UI__ClientsPageQuery,
          GraphQLTypes.UI__ClientsPageQueryVariables
        >
          query={clientsPageQuery}
          variables={{
            serviceId: graphId,
            timeTo:
              date.getDateBounds(selectedRange, {
                from: timeFrom,
                to: timeTo,
              }).timeTo || '-0',
            timeFrom:
              date.getDateBounds(selectedRange, {
                from: timeFrom,
                to: timeTo,
              }).timeFrom || '-3600',
            filter,
          }}
          notifyOnNetworkStatusChange={true}
        >
          {({ data, error, networkStatus }) => {
            if (error || currentPlan.error) {
              console.error(error || currentPlan.error); // eslint-disable-line no-console
              throw error || currentPlan.error;
            }

            if (
              [NetworkStatus.loading, NetworkStatus.setVariables].includes(
                networkStatus,
              ) ||
              currentPlan.loading
            ) {
              // TODO: loader
              return <Loading />;
            }

            if (!data || !data.service) {
              throw new Error('no data');
            }

            const queryStats = data.service.stats?.queryStats;

            const totalRequestsCount = sumBy(
              queryStats,
              (queryStat) => queryStat.metrics.totalRequestCount,
            );

            const clientsQueryStats = flow(
              (queryStatsToGroupOn: typeof queryStats) =>
                groupBy(
                  queryStatsToGroupOn,
                  (queryStat) => queryStat.groupBy.clientName,
                ),
              (queryStatsKeyedOnClientName) =>
                map(
                  queryStatsKeyedOnClientName,
                  (queryStatsForASpecificClient) => ({
                    clientName:
                      queryStatsForASpecificClient[0]?.groupBy.clientName ??
                      UNIDENTIFIED_CLIENT,
                    queryStats: queryStatsForASpecificClient,
                  }),
                ),

              (clients) =>
                orderBy(
                  clients,
                  (client) =>
                    sumBy(
                      client.queryStats,
                      (queryStat) => queryStat.metrics.totalRequestCount,
                    ),
                  ['desc'],
                ),
            )(queryStats);

            // If we have at least one registered operation across all of the
            // query stats, then we'll show registration information
            const graphHasRegisteredOperationsReported = Boolean(
              queryStats?.find(
                ({ metrics: { registeredOperationCount } }) =>
                  registeredOperationCount !== 0,
              ),
            );

            // set a default selected client
            if (clientName === undefined && clientsQueryStats[0]) {
              const { clientName: name } = clientsQueryStats[0];

              return (
                <Redirect
                  to={clientsRouteConfig.location({
                    ...routeParams,
                    range: undefined,
                    graphId,
                    graphVariant,
                    clientName: name,
                    fastModeRange: undefined,
                  })}
                />
              );
            }

            return (
              <>
                <Modal
                  isOpen={instructionModal}
                  size="xl"
                  onClose={() => setInstructionModal(false)}
                >
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>
                      <ModalTitle>Set up client awareness</ModalTitle>
                      <ModalDescription>
                        Tag requests from your clients with name and version
                        metadata to look at your graph's usage information
                        segmented by client.
                      </ModalDescription>
                    </ModalHeader>
                    <ModalBody>
                      <p>
                        Configure in <b>Apollo Client</b>:
                      </p>
                      <Code
                        showLineNumbers={true}
                        className="shrink-0"
                        highlight={[5, 6]}
                        code={APOLLO_CLIENT_INSTRUCTIONS}
                      />
                      <div className="mt-4">
                        Configure in <b>Apollo Server</b>:
                      </div>
                      <Code
                        showLineNumbers={true}
                        className="shrink-0"
                        highlight={[2, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]}
                        code={APOLLO_SERVER_INSTRUCTIONS}
                      />
                      <div className="mt-4">
                        Not using Apollo Client or Apollo Server?{' '}
                        <Link
                          isExternal
                          to={DocsRouter.path('ClientAwareness')}
                        >
                          Visit our docs
                        </Link>{' '}
                        to learn how to set up client awareness in your stack.
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        variant="primary"
                        onClick={() => setInstructionModal(false)}
                      >
                        Close
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
                <PageTitle
                  title="Clients"
                  rightSide={
                    <MetricsFilter
                      selectedRange={selectedRange}
                      maxRangeInDays={accountCapabilities.maxRangeInDays}
                      ranges={
                        accountCapabilities.ranges as ReadonlyArray<TimeRangeOption>
                      }
                    />
                  }
                />
                <InsightsEnforcementWarning className="mx-8 my-4" />
                {clientsQueryStats.length === 0 ? (
                  <Alert status="info" className="mx-8 my-4 w-auto">
                    There is <b>no request activity</b> from your graph in the{' '}
                    {printTimeRangeLabel(selectedRange).toLocaleLowerCase()}.
                    Try adjusting the time range or configuring metrics
                    observability in your server.
                  </Alert>
                ) : (
                  <>
                    <div className="flex flex-1 overflow-y-auto">
                      <div className="hide-scrollbar flex h-full flex-1 flex-col overflow-hidden overflow-y-auto border-r border-primary">
                        <Heading className="mx-8 mb-4 mt-8" size="2xl">
                          Activity in the{' '}
                          {printTimeRangeLabel(
                            selectedRange,
                          ).toLocaleLowerCase()}
                        </Heading>

                        {clientsQueryStats.map((clientQueryStats, i) => (
                          <Fragment key={clientQueryStats.clientName}>
                            {clientsQueryStats.length === 1 &&
                              !clientsQueryStats[0]?.clientName && (
                                <Alert
                                  status="info"
                                  className="mx-8 mt-8 w-auto shrink-0"
                                >
                                  Your graph's requests are currently being sent
                                  anonymously. We recommend{' '}
                                  <Button
                                    variant="buttonLink"
                                    onClick={() => setInstructionModal(true)}
                                  >
                                    instrumenting client awareness in your
                                    server
                                  </Button>{' '}
                                  so you can view your usage insights in Studio
                                  on a per-client basis.
                                </Alert>
                              )}

                            <Client
                              {...clientQueryStats}
                              totalRequestsCount={totalRequestsCount}
                              className={i !== 0 ? 'mt-8' : ''}
                              canViewVersions={
                                currentPlan.currentPlan?.clientVersions ?? false
                              }
                              selectedClientName={clientName}
                              hasScrolledToClient={hasScrolledToClient}
                              onScrollIntoView={() => {
                                setHasScrolledToClient(true);
                              }}
                              hasClientNameOnPageLoad={
                                !!initialClientName.current
                              }
                            />
                          </Fragment>
                        ))}
                      </div>
                      <div className="hide-scrollbar flex flex-1 flex-col overflow-hidden overflow-y-auto">
                        {clientName === undefined ? (
                          <EmptySelectionPrompt message="Select a client or version on the left to see client details." />
                        ) : (
                          <ClientDetails
                            graphId={graphId}
                            clientName={clientName}
                            clientVersion={clientVersion}
                            graphHasRegisteredOperationsReported={
                              graphHasRegisteredOperationsReported
                            }
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            );
          }}
        </Query>
      )}
    </FilterProvider>
  );
};
